import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import { wardrobeKeys } from './wardrobe';
import { walletKeys } from './wallet';

// Server: equipment/EquippedSlotDto.java — one occupied equipment slot's projection. `hasTransmog`
// is true when the equipped item's stats_json.cosmetic field is present (there's art to toggle at
// all); `transmogVisible` is the stored on/off state itself. Server sends `null` for an unoccupied
// slot rather than omitting the key (EquipmentStateDto's own javadoc) — see EquipmentStateDto below.
export type EquippedSlotEntryDto = {
  itemCode: string;
  name: string;
  rarity: string;
  transmogVisible: boolean;
  hasTransmog: boolean;
} | null;

// Server: equipment/EffectiveStats.java — base stats plus the summed bonuses of everything
// equipped, deliberately uncapped past StatService.allocate's training hard cap of 100 (gear is
// allowed to push effective power past it — see that record's own javadoc). `ward` (combat-rebuild
// Task 20 fix round: this type had drifted from the wire — the server record already carries it,
// `int ward` between `guard` and `speed`) is the magical-defense counterpart to `guard`.
export type EffectiveStatsDto = {
  vitality: number;
  power: number;
  focus: number;
  guard: number;
  ward: number;
  speed: number;
  fortune: number;
};

// Server: equipment/EquipmentStateDto.java — GET/PUT /api/v1/companions/{id}/equipment[/{slot}].
// `slots` always has one entry per enabled equipment slot type (currently the 6 functional slots —
// weapon/offhand/head/body/trinket/charm), null value for an unoccupied slot.
export type EquipmentStateDto = {
  slots: Record<string, EquippedSlotEntryDto>;
  effectiveStats: EffectiveStatsDto;
  hp: number;
};

export const equipmentKeys = {
  all: ['equipment'] as const,
  byCompanion: (companionId: number) => ['equipment', companionId] as const,
};

export const getEquipment = (companionId: number) =>
  apiFetch<EquipmentStateDto>(`/companions/${companionId}/equipment`);

export function useEquipment(companionId: number | undefined) {
  return useQuery({
    queryKey: equipmentKeys.byCompanion(companionId ?? -1),
    queryFn: () => getEquipment(companionId as number),
    enabled: companionId != null,
  });
}

// PUT /companions/{id}/equipment/{slot} body {itemCode} — EquipmentController.EquipRequest.
// itemCode null unequips that slot; non-null equips (or swaps) it, refusing (equipment_level_locked
// 409 / item_already_equipped 409 / not_owned 400 / slot_mismatch 400 — EquipmentService) a companion
// that hasn't reached the item's level requirement, an item already worn by another companion, an
// item the account doesn't own, or a slot the item doesn't fit.
export const putEquipment = (companionId: number, slot: string, itemCode: string | null) =>
  apiFetch<EquipmentStateDto>(`/companions/${companionId}/equipment/${slot}`, {
    method: 'PUT',
    body: JSON.stringify({ itemCode }),
  });

export type EquipInput = { companionId: number; slot: string; itemCode: string | null };

/**
 * NOT optimistic — equip/unequip has real, unpredictable server refusals (equipment_level_locked,
 * item_already_equipped, not_owned) and changes battle-facing stats, so a wrong optimistic guess is
 * a worse miss than wardrobe's purely-cosmetic usePutOutfit. Invalidates this companion's equipment
 * cache AND wardrobeKeys.detail (the wardrobe/compositor query — HabitatScreen's live Compositor
 * reads companion art from useWardrobe, same "art" module as bag.ts's useUseItem/companions.ts) —
 * equipping a piece with a currently-visible transmog projection changes what the paper-doll renders
 * even though nothing in companion_cosmetics itself changed (WardrobeDtos.EquippedSlotDto's
 * `projected` flag, layerOrder.ts's EquippedSlot type).
 */
export function useEquip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EquipInput) => putEquipment(input.companionId, input.slot, input.itemCode),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: equipmentKeys.byCompanion(variables.companionId) });
      queryClient.invalidateQueries({ queryKey: wardrobeKeys.detail(variables.companionId) });
    },
  });
}

// POST /companions/{id}/equipment/{slot}/transmog body {visible, idempotencyKey} —
// EquipmentController.TransmogRequest / TransmogService.toggle. Toggling ON costs
// TransmogService.TRANSMOG_FEE (75 Shells) unless the slot is already visible (free no-op);
// toggling OFF is always free. Refuses with no_transmog_art (409) if the equipped item has no
// linked cosmetic art to project.
export const postTransmog = (companionId: number, slot: string, visible: boolean) =>
  apiFetch<EquipmentStateDto>(`/companions/${companionId}/equipment/${slot}/transmog`, {
    method: 'POST',
    body: JSON.stringify({ visible, idempotencyKey: crypto.randomUUID() }),
  });

export type TransmogInput = { companionId: number; slot: string; visible: boolean };

/**
 * NOT optimistic, same class as useEquip above — plus toggling ON is currency-gated (like
 * usePurchase/useStartTraining), so no onMutate. `idempotencyKey` is generated fresh per
 * mutate() call inside the mutationFn, same "fresh key, no shared crypto.randomUUID() call site"
 * shape as shops.ts's usePurchase, so a react-query retry of a failed attempt is still safe (a
 * genuine retry reuses the SAME generated key only within one mutationFn invocation, not across
 * separate mutate() calls). Invalidates equipment (transmogVisible flips), wardrobe/compositor
 * (the projected cosmetic layer appears/disappears from the live paper-doll render — same
 * wardrobeKeys.detail as useEquip), and wallet (the fee, when charged — mirrors the fix-wave
 * lesson from world.ts's useForage/companions.ts's useCreateCompanion: any mutation that can move
 * Shells must invalidate walletKeys or the HUD balance goes stale).
 */
export function useTransmog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TransmogInput) => postTransmog(input.companionId, input.slot, input.visible),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: equipmentKeys.byCompanion(variables.companionId) });
      queryClient.invalidateQueries({ queryKey: wardrobeKeys.detail(variables.companionId) });
      queryClient.invalidateQueries({ queryKey: walletKeys.all });
    },
  });
}
